import React from 'react';
import _ from 'lodash';
import { PREVIEW_PASS_5, PREVIEW_PASS_60 } from 'helpers/AuthHelper';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Loader from 'components/Loader';
import ProviderGroup from '../ProviderGroup';
import Provider from '../Provider';
import Style from './ProvidersList.module.scss';

const ProvidersList = props => {
  const {
    isLive,
    moveSideMenuToTop,
    mvpdProviders,
    onProviderClick,
    previewPassExpired,
    previewPassSignIn,
    profileId,
    query,
    scrollToTop,
    selectedChannel,
  } = props;

  if (!mvpdProviders || !mvpdProviders.length) return <Loader center />;
  const getGroupedProviders = providers =>
    // return an object with keys set for each first character
    providers.reduce((acc, item) => {
      const firstCharacterUppercase = _.toUpper(_.get(item, 'nameLowercase.0'));
      // we use isFinite to avoid NaN returning true
      const isNumber = _.isFinite(parseInt(firstCharacterUppercase, 10));
      // all numbers are grouped under the # key
      const groupKey = isNumber ? '#' : firstCharacterUppercase;
      if (groupKey) {
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(item);
      }
      return acc;
    }, {});
  const allProvidersList = groupedProviders => {
    // create an array of the keys so we can loop through the providers
    const keys = Object.keys(groupedProviders);
    const providerGroups = (
      <div>
        {keys.map(key => (
          <ProviderGroup
            key={key}
            groupName={key}
            providers={groupedProviders[key]}
            onProviderClick={onProviderClick}
            selectedChannel={selectedChannel}
            isLive={isLive}
            previewPassExpired={previewPassExpired}
            previewPassSignIn={previewPassSignIn}
            profileId={profileId}
          />
        ))}
      </div>
    );
    return (
      <div>
        <div className={classNames(Style.providersDirectory, moveSideMenuToTop && Style.top)}>
          {keys.map(key => (
            <button
              key={`directory-item-${key}`}
              aria-label={`scroll to providers starting with ${key}`}
              type="button"
              onClick={() => {
                document
                  .querySelector(`[providers-group="${key}"]`)
                  .scrollIntoView({ block: 'start', behavior: 'smooth' });
              }}
              className={Style.providersDirectoryKey}
            >
              {key}
            </button>
          ))}
          <button
            aria-label="scroll to top of page"
            type="button"
            onClick={scrollToTop}
            className={classNames(Style.providersDirectoryKey, Style.scrollToTop)}
          >
            Top
          </button>
        </div>
        {providerGroups}
      </div>
    );
  };
  const removePreviewPassProviders = providers => {
    const previewPasses = [PREVIEW_PASS_5, PREVIEW_PASS_60];
    const providersWithoutPreviewPass = _.clone(providers);
    _.remove(providersWithoutPreviewPass, mvpd => {
      const id = _.get(mvpd, 'adobePassId');
      return _.includes(previewPasses, id);
    });
    return providersWithoutPreviewPass;
  };
  const filterProvidersByQuery = (query, providers) =>
    _.filter(providers, provider => {
      const lowercaseInput = query.toLowerCase();
      const name = _.get(provider, 'name', '').toLowerCase();
      const alternateNames = _.get(provider, 'alternateNames', []);
      const nameMatch = name.indexOf(lowercaseInput) > -1;
      const alternateNamesMatch = _.find(
        alternateNames,
        n => n.toLowerCase().indexOf(lowercaseInput) > -1,
      );
      return nameMatch || alternateNamesMatch || false;
    }).slice(0, 7);

  const filteredProviders = removePreviewPassProviders(mvpdProviders);
  let providers;

  if (query !== '') {
    const queryFilteredProviders = filterProvidersByQuery(query, filteredProviders);
    if (!queryFilteredProviders.length) {
      const { auth_noProvidersText: noProvidersText } = window.Language;
      providers = <div className={Style.noProvidersFound}>{noProvidersText}</div>;
    } else {
      providers = queryFilteredProviders.map(provider => (
        <Provider
          key={_.get(provider, 'name')}
          {...provider}
          onProviderClick={onProviderClick}
          selectedChannel={selectedChannel}
        />
      ));
    }
  } else {
    const providersByGroup = getGroupedProviders(filteredProviders);
    providers = allProvidersList(providersByGroup);
  }
  return <div key="providers">{providers}</div>;
};

ProvidersList.defaultProps = {
  isLive: null,
  moveSideMenuToTop: false,
  mvpdProviders: null,
  onProviderClick: () => {},
  previewPassExpired: null,
  previewPassSignIn: false,
  profileId: null,
  selectedChannel: null,
};
ProvidersList.propTypes = {
  isLive: PropTypes.bool,
  moveSideMenuToTop: PropTypes.bool,
  mvpdProviders: PropTypes.arrayOf(PropTypes.shape({})),
  onProviderClick: PropTypes.func,
  previewPassExpired: PropTypes.bool,
  previewPassSignIn: PropTypes.bool,
  profileId: PropTypes.string,
  query: PropTypes.string.isRequired,
  scrollToTop: PropTypes.func.isRequired,
  selectedChannel: PropTypes.number,
};

export default ProvidersList;
